import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

// ── Payload Types ─────────────────────────────────────────
export interface SubscriptionEntry {
  id: string;
  user_id: string;
  service_name: string;
  service_category: string | null;
  cost_amount: number;
  cost_currency: string;
  billing_cycle: string;
  billing_date: string | null;
  next_billing_date: string | null;
  status: string;
  auto_renewal: boolean;
  payment_method: string | null;
  vendor_url: string | null;
  created_at: string;
}

export interface SubscriptionStatsPayload {
  totalSubscriptions: number;
  activeSubscriptions: number;
  monthlySpend: number;
  currency: string;
}

export interface SubscriptionListPayload {
  subscriptions: SubscriptionEntry[];
  total: number;
}

// ── Server Functions ──────────────────────────────────────
export const getSubscriptions = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      page: z.number().int().min(1).default(1),
      limit: z.number().int().min(1).max(100).default(20),
      status: z.enum(["active", "cancelled", "paused", "trial"]).optional(),
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const offset = (data.page - 1) * data.limit;

    let query = client
      .from("subscriptions")
      .select("id, user_id, service_name, service_category, cost_amount, cost_currency, billing_cycle, billing_date, next_billing_date, status, auto_renewal, payment_method, vendor_url, created_at", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + data.limit - 1);

    if (data.status) {
      query = query.eq("status", data.status);
    }

    const { data: subs, error, count } = await query;

    if (error) return { data: null, error: error.message };

    const entries: SubscriptionEntry[] = (subs ?? []).map((s) => ({
      id: String(s.id),
      user_id: String(s.user_id),
      service_name: String(s.service_name),
      service_category: s.service_category ? String(s.service_category) : null,
      cost_amount: Number(s.cost_amount) || 0,
      cost_currency: String(s.cost_currency ?? "AUD"),
      billing_cycle: String(s.billing_cycle ?? "monthly"),
      billing_date: s.billing_date ? String(s.billing_date) : null,
      next_billing_date: s.next_billing_date ? String(s.next_billing_date) : null,
      status: String(s.status ?? "active"),
      auto_renewal: Boolean(s.auto_renewal),
      payment_method: s.payment_method ? String(s.payment_method) : null,
      vendor_url: s.vendor_url ? String(s.vendor_url) : null,
      created_at: String(s.created_at),
    }));

    return { data: { subscriptions: entries, total: count ?? 0 }, error: null };
  });

export const getSubscriptionStats = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const { data: subs, error, count } = await client
      .from("subscriptions")
      .select("cost_amount, cost_currency, billing_cycle, status", { count: "exact" });

    if (error) return { data: null, error: error.message };

    const list = (subs ?? []) as Array<{ cost_amount: number; cost_currency: string; billing_cycle: string; status: string }>;
    const activeList = list.filter((s) => s.status === "active");

    const monthlySpend = activeList.reduce((sum, s) => {
      const amount = Number(s.cost_amount) || 0;
      if (s.billing_cycle === "yearly") return sum + amount / 12;
      if (s.billing_cycle === "quarterly") return sum + amount / 3;
      return sum + amount;
    }, 0);

    const payload: SubscriptionStatsPayload = {
      totalSubscriptions: count ?? 0,
      activeSubscriptions: activeList.length,
      monthlySpend: Math.round(monthlySpend * 100) / 100,
      currency: "AUD",
    };

    return { data: payload, error: null };
  });
